import React, { useCallback, useRef } from 'react';
import { Modal, Space, Input, Button, QRCode, message } from 'antd';
import { CopyOutlined, DownloadOutlined } from '@ant-design/icons';

import { copyTextToClipboard } from '../../utils/clipboard';
import { qrValue } from '../../utils/shareUrls';
import { dataUrlToBlob } from '../../utils/dataUrlBlob';
import { anchorOf, saveFile } from '../../platform';

const ShareModal = ({
	open = false,
	onClose,
	modalTitle = 'Share',
	// `preview` takes a CardBadge (preferred); the eyebrow/title/icon trio is
	// the older shape, kept for callers that have no card record to hand.
	preview = null,
	eyebrow,
	previewTitle = 'Untitled',
	previewIcon = null,
	shareUrl = '',
	downloadFileName = 'keepr-share',
	className = 'item-share-modal',
	// A warning shown above the link — for links that can never be read back
	// (a share-link code exists exactly once, in its create response).
	note = null,
	// An extra section below the QR code. Every share surface uses it for the
	// same thing — TempShareLinkSection, which mints a public link to whatever
	// this modal is sharing — but the modal stays generic and just renders it.
	extra = null,
}) => {
	const qrWrapperRef = useRef(null);
	const resolvedDownloadName = downloadFileName || 'keepr-share';

	const handleCopyUrl = useCallback(async () => {
		if (!shareUrl) return;
		if (await copyTextToClipboard(shareUrl)) message.success('Link copied to clipboard');
		else message.error('Unable to copy link');
	}, [shareUrl]);

	const handleDownloadQr = useCallback(async (event) => {
		const anchor = anchorOf(event);
		if (!shareUrl || typeof document === 'undefined') return;
		const canvas = qrWrapperRef.current?.querySelector('canvas');
		if (!canvas) {
			message.error('QR code is not ready yet');
			return;
		}
		try {
			await saveFile(dataUrlToBlob(canvas.toDataURL('image/png')), `${resolvedDownloadName}.png`, { anchor });
		} catch {
			message.error('Could not save the QR code');
		}
	}, [shareUrl, resolvedDownloadName]);

	const modalClassName = ['share-modal', className].filter(Boolean).join(' ').trim();

	return (
		<Modal
			open={open}
			onCancel={onClose}
			footer={null}
			title={modalTitle}
			centered
			className={modalClassName}
			destroyOnClose
		>
			<div className="item-share-modal__preview">
				{preview || (
					<div className="item-share-modal__preview-header">
						{previewIcon}
						<div className="record-identity__main">
							{eyebrow ? <div className="micro-label">{eyebrow}</div> : null}
							<div className="item-share-modal__preview-title">{previewTitle}</div>
						</div>
					</div>
				)}
			</div>

			{note ? <div className="item-share-modal__note">{note}</div> : null}

			<div className="item-share-modal__section">
				<div className="item-share-modal__section-title">Direct link</div>
				<Space.Compact block>
					<Input value={shareUrl} readOnly />
					<Button
						type="primary"
						icon={<CopyOutlined />}
						onClick={handleCopyUrl}
						disabled={!shareUrl}
					>
						Copy
					</Button>
				</Space.Compact>
			</div>

			<div className="item-share-modal__section">
				<div className="item-share-modal__section-title">QR code</div>
				<div className="item-share-modal__qr">
					<div className="item-share-modal__qr-preview" ref={qrWrapperRef}>
						{/* A record's bare short URL in capitals: a smaller code (utils/shareUrls qrValue). */}
						<QRCode value={qrValue(shareUrl) || ' '} bordered={false} size={160} />
					</div>
					<Button
						icon={<DownloadOutlined />}
						onClick={handleDownloadQr}
						disabled={!shareUrl}
					>
						Download QR
					</Button>
				</div>
			</div>

			{extra}
		</Modal>
	);
};

export default ShareModal;
